import React from 'react';
import PropTypes from 'prop-types';

import Styles from './styles';

const HORIZONTAL_MARGIN = 70;

const Circle = ({ fill, ...rest }) => <circle style={{ fill }} {...rest} />;

Circle.defaultProps = {
  fill: '#eee',
  onClick: () => {}
};

Circle.propTypes = {
  cx: PropTypes.number.isRequired,
  cy: PropTypes.number.isRequired,
  r: PropTypes.number.isRequired,
  fill: PropTypes.string,
  onClick: PropTypes.func
};

const CircleText = ({ fontSize, x, y, text, leftSide, rightSide, topSide, horizontal }) => {
  let axisV = x;
  let axisH = y;
  let textAnchor = 'middle';
  let alignmentBaseline = 'middle';
  if (horizontal) {
    axisH = topSide + fontSize + 10;
  } else if (rightSide) {
    axisV = leftSide + 10;
    textAnchor = 'left';
    alignmentBaseline = 'left';
  }
  return (
    <text
      x={axisV}
      y={axisH}
      fontSize={fontSize}
      fontFamily="Arial"
      textAnchor={textAnchor}
      alignmentBaseline={alignmentBaseline}
    >
      {text}
    </text>
  );
};

CircleText.defaultProps = {
  rightSide: true,
  horizontal: false
};

CircleText.propTypes = {
  text: PropTypes.string.isRequired,
  x: PropTypes.number.isRequired,
  y: PropTypes.number.isRequired,
  fontSize: PropTypes.number.isRequired,
  leftSide: PropTypes.number.isRequired,
  rightSide: PropTypes.bool,
  topSide: PropTypes.number.isRequired,
  horizontal: PropTypes.bool
};

const Line = ({ width, fill, ...rest }) => (
  <line
    style={{
      stroke: fill,
      strokeWidth: `${width}px`,
      zIndex: -10
    }}
    {...rest}
  />
);

Line.defaultProps = {
  fill: '#eee'
};

Line.propTypes = {
  x1: PropTypes.number.isRequired,
  x2: PropTypes.number.isRequired,
  y1: PropTypes.number.isRequired,
  y2: PropTypes.number.isRequired,
  width: PropTypes.number.isRequired,
  fill: PropTypes.string
};

const SvgMilestones = ({
  elements,
  height,
  width,
  radioRatio,
  lineWidthRatio,
  fontSize,
  cChecked,
  cUnchecked,
  cSelected,
  cBorder,
  rightSide,
  horizontal
}) => {
  const numberOfCircles = elements.length;
  const magicNumber = Math.round(height / numberOfCircles);

  const innerRadius = horizontal
    ? Math.round(height * radioRatio)
    : Math.round((magicNumber / 2) * radioRatio);
  const lineWidth = horizontal
    ? Math.round(height * lineWidthRatio)
    : Math.round(magicNumber * lineWidthRatio);

  const lineLength = horizontal
    ? width - 2 * (innerRadius + lineWidth + HORIZONTAL_MARGIN)
    : height - 2 * (innerRadius + lineWidth);

  const centroidsDis = Math.round(lineLength / (numberOfCircles - 1));

  const svgElements = elements.map((item, index, array) => {
    const backColor = item.checked ? cChecked : cUnchecked;
    const borderColor = item.selected ? cSelected : cBorder;
    const lineColor = index < array.length - 1 && array[index + 1].selected ? cSelected : cBorder;

    let lineP1XY = [innerRadius + lineWidth, innerRadius + lineWidth + centroidsDis * index];
    let lineP2XY = [innerRadius + lineWidth, innerRadius + lineWidth + centroidsDis * (index + 1)];
    if (horizontal) {
      lineP1XY = [
        innerRadius + lineWidth + HORIZONTAL_MARGIN + centroidsDis * index,
        innerRadius + lineWidth
      ];
      lineP2XY = [
        innerRadius + lineWidth + HORIZONTAL_MARGIN + centroidsDis * (index + 1),
        innerRadius + lineWidth
      ];
    }

    const leftSide = lineP1XY[0] + innerRadius + lineWidth;
    const topSide = innerRadius + lineWidth;

    const indexKey = `${index}`;
    return (
      <React.Fragment key={`stepId${indexKey}`}>
        {index < array.length - 1 && (
          <Line
            key={`idILine${indexKey}`}
            x1={lineP1XY[0]}
            y1={lineP1XY[1]}
            x2={lineP2XY[0]}
            y2={lineP2XY[1]}
            width={lineWidth}
            fill={lineColor}
          />
        )}
        <Circle
          key={`idICircA${indexKey}`}
          cx={lineP1XY[0]}
          cy={lineP1XY[1]}
          r={innerRadius + lineWidth}
          fill={borderColor}
          onClick={item.callback}
        />
        <Circle
          key={`idICircB${indexKey}`}
          cx={lineP1XY[0]}
          cy={lineP1XY[1]}
          r={innerRadius}
          fill={backColor}
          onClick={item.callback}
        />
        <CircleText
          key={`idIText${indexKey}`}
          fontSize={fontSize}
          x={lineP1XY[0]}
          y={lineP1XY[1]}
          text={item.text}
          leftSide={leftSide}
          rightSide={rightSide}
          topSide={topSide}
          horizontal={horizontal}
        />
      </React.Fragment>
    );
  });
  return (
    <Styles.SVGMilestones horizontal={horizontal} width={width} height={height}>
      <svg
        x="0px"
        y="0px"
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="xMinYMin meet"
        className="mx-auto"
      >
        {svgElements}
      </svg>
    </Styles.SVGMilestones>
  );
};

SvgMilestones.defaultProps = {
  cChecked: '#5cb85c',
  // cWarning: '#f0ad4e',
  cUnchecked: '#d9534f',
  cBorder: '#eee',
  cSelected: '#337ab7',
  lineWidthRatio: 0.03, // keep relation with radioRatio
  radioRatio: 0.2, // if = 1 circles will touch between them
  fontSize: 16,
  height: 600,
  width: 200,
  rightSide: true,
  horizontal: false
};

SvgMilestones.propTypes = {
  cChecked: PropTypes.string,
  // cWarning: PropTypes.string,
  cUnchecked: PropTypes.string,
  cBorder: PropTypes.string,
  cSelected: PropTypes.string,
  lineWidthRatio: PropTypes.number,
  // divIdContainer: PropTypes.number,
  radioRatio: PropTypes.number,
  fontSize: PropTypes.number,
  height: PropTypes.number,
  width: PropTypes.number,
  elements: PropTypes.array.isRequired,
  rightSide: PropTypes.bool,
  horizontal: PropTypes.bool
};

export default SvgMilestones;
